import type { ResponseError } from '@app/utils/types'

import { isResponseError } from '@app/utils/predicates'

function* exponentialBackoffGenerator(
  baseDelay: number,
  maxRetries: number,
  expotentialBase: number
): Generator<number> {
  let totalDelay = baseDelay
  yield totalDelay

  for (let retry = 1; retry < maxRetries; retry++) {
    totalDelay += Math.floor(Math.pow(expotentialBase, retry - 1))
    yield totalDelay
  }
}

const jitter = (maxNumber: number): number => {
  return Math.floor(Math.random() * maxNumber)
}

const PREDICATE_ERROR =
  'SFetch::WithRetry::Should retry the request based on the predicate'

const MAX_RETRIES_ERROR = 'SFetch::WithRetry::Max retries exceeded'

/**
 * A function that retries a callback function with exponential backoff
 * @param base The base delay in milliseconds
 * @param callback The callback function to retry
 * @param expotentialBase The exponential base
 * @param maxRetries The maximum number of retries
 * @param shouldRetry A function that determines if the error should be retried
 * @returns The return value of the callback function if it succeeds or the last error if it fails
 */
export function withRetry<P extends Array<unknown>, R>(
  callback: (...args: P) => Promise<R> | R,
  {
    base = 500,
    expotentialBase = 2,
    maxRetries = 2,
    shouldRetry,
  }: {
    maxRetries?: number
    base?: number
    expotentialBase?: number
    shouldRetry?: (response: R | ResponseError) => boolean
  }
): (...args: P) => Promise<R> {
  return async function (...args: P) {
    try {
      const response = await callback(...args)

      if (shouldRetry && shouldRetry(response)) {
        throw new Error(PREDICATE_ERROR)
      }

      return response
    } catch (e) {
      if (shouldRetry && isResponseError(e) && !shouldRetry(e)) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw e
      }

      const backoffGenerator = exponentialBackoffGenerator(
        base / 1000,
        maxRetries,
        expotentialBase
      )

      for (const delay of backoffGenerator) {
        try {
          const response = (await callback(...args)) as R

          if (shouldRetry && shouldRetry(response)) {
            throw new Error(PREDICATE_ERROR)
          }

          return response
        } catch {
          if (delay !== undefined) {
            // We also add some randomised jitter to the delay to avoid thundering herd issues
            await new Promise((resolve) =>
              setTimeout(resolve, delay * 1000 + jitter(2000))
            )
          }
        }
      }

      throw new Error(MAX_RETRIES_ERROR)
    }
  }
}
